/**
 * SpatialMixer (§3.5, §3.6): one PannerNode (HRTF, direction only) + one GainNode (custom curve)
 * per subscribed peer. The nodes are handed to livekit-client via
 * `RemoteAudioTrack.setWebAudioPlugins([panner, gain, sink])`.
 *
 * Final gain = distanceGain x entryRamp x peerVolume x master   (§3.3)
 *
 * Preferenze di ascolto (applicate subito, senza ricreare i nodi):
 *   - stereo (default): panner HRTF, direzione 3D;
 *   - mono: panner neutralizzato (equal-power, sorgente sempre davanti: stessa voce nelle due
 *     orecchie) con +3 dB per compensare il pan equal-power al centro;
 *   - effetto prossimità off: volume pieno nel raggio d'ascolto, sfumatura negli ultimi 2 blocchi
 *     (flatGain). Radio di squadra, volumi, deafen e ducking si applicano come sempre.
 *
 * Uscita (anti-eco): livekit-client collega l'ultimo nodo dei plugin a un suo GainNode e quello,
 * senza alternative, a `ctx.destination`. Su Chromium l'AEC di getUserMedia NON usa come
 * riferimento l'audio che esce da `ctx.destination`, quindi chi usa le casse rimanda l'eco agli
 * altri. Per questo:
 *   - il gain di ogni peer va anche nel bus d'uscita `output` (GainNode del ducking), che il
 *     VoiceController collega a un MediaStreamAudioDestinationNode riprodotto da un elemento
 *     <audio> (riferimento corretto per l'AEC);
 *   - l'ultimo plugin passato a livekit è un `sink` con guadagno 0: verso `ctx.destination`
 *     arriva solo silenzio, così le voci non si sentono due volte.
 * Panner, gain per distanza, volume master e per giocatore e deafen restano identici.
 *
 * Updates run in requestAnimationFrame, or on a 30 Hz Web Worker ticker when the tab is hidden
 * (rAF paused, page timers throttled by Chrome: see backgroundTicker.ts).
 * Audio params are smoothed with setTargetAtTime(τ = 30 ms); teleports snap with setValueAtTime.
 */
import { startBackgroundTicker, type Ticker } from './backgroundTicker';
import {
  DEFAULT_GAIN,
  DEFAULT_INTERP_DELAY_MS,
  SPATIAL_FLAG_POLICY_MUTED,
  SPATIAL_FLAG_RADIO,
  type AudioMode,
  type GainConfig,
  type SpatialMessage,
} from '../api/protocol';
import { FRONT, RADIO_FLOOR_GAIN, length, pannerPosition, proximityGain, type Vec3 } from './math';
import { ServerClock, SpatialBuffer } from './SpatialBuffer';

export const ENTRY_RAMP_MS = 150;
export const PARAM_TIME_CONSTANT_S = 0.03;
/** Ducking: la rampa raggiunge ~95% del valore in 3 costanti di tempo. */
export const DUCK_ATTACK_MS = 50;
export const DUCK_RELEASE_MS = 400;
export const DUCK_ATTACK_TAU_S = DUCK_ATTACK_MS / 1000 / 3;
export const DUCK_RELEASE_TAU_S = DUCK_RELEASE_MS / 1000 / 3;
/** Mono: l'equal-power al centro dà cos(π/4) per orecchio; questo fattore riporta il livello a 1. */
export const MONO_GAIN_COMPENSATION = Math.SQRT2;
/** Riduzione predefinita delle voci in arrivo mentre parli (0..1). */
export const DEFAULT_DUCK_AMOUNT = 0.5;

/** Guadagno del bus d'uscita: 1 senza ducking, 1 - amount mentre si parla. */
export function duckGain(active: boolean, amount: number): number {
  return active ? 1 - clamp(amount, 0, 1) : 1;
}
const HIDDEN_TICK_MS = 33;
const EPS = 1e-4;
/** Identities absent from frames for longer than this are no longer reported. */
const STALE_MS = 1000;

export interface PeerAudioState {
  identity: string;
  /** Distance in blocks, null if unknown (no spatial data yet / global mode). */
  distance: number | null;
  /** Current target gain (0..1+, before the master volume). */
  gain: number;
  /** Listener-space position (x right, z front negative). */
  position: Vec3;
  radio: boolean;
  policyMuted: boolean;
}

interface PeerNodes {
  key: string;
  identity: string;
  panner: PannerNode;
  gain: GainNode;
  /** Terminale a guadagno 0 verso cui livekit collega il suo GainNode -> ctx.destination. */
  sink: GainNode;
  addedAt: number;
  /** First time the peer had a non-zero target gain: start of the entry ramp. */
  audibleSince: number | null;
  lastGain: number;
  lastPos: Vec3;
  lastTeleportAt: number | null;
  state: PeerAudioState;
}

export interface SpatialMixerOptions {
  gain?: GainConfig;
  interpDelayMs?: number;
  /** Clock for tests (ms). Defaults to performance.now(). */
  now?: () => number;
  /** Wall clock used to align with server time (ms epoch). Defaults to Date.now(). */
  wallNow?: () => number;
}

export class SpatialMixer {
  private readonly peers = new Map<string, PeerNodes>();
  private readonly buffer = new SpatialBuffer();
  private readonly clock = new ServerClock();
  private gainCfg: GainConfig;
  private interpDelayMs: number;
  private mode: AudioMode = 'spatial';
  private master = 1;
  private deafened = false;
  private readonly peerVolume = new Map<string, number>();
  private readonly peerMuted = new Set<string>();
  private readonly radioPeers = new Set<string>();
  private listenerYaw = 0;
  private lastRenderT: number | null = null;
  private running = false;
  private rafId: number | null = null;
  private ticker: Ticker | null = null;
  private readonly now: () => number;
  private readonly wallNow: () => number;
  private monitor: { bus: GainNode; left: AnalyserNode; right: AnalyserNode } | null = null;
  /** Bus d'uscita dopo i gain dei peer (somma di tutte le voci); il suo guadagno fa il ducking. */
  readonly output: GainNode;
  private duckActive = false;
  private duckAmount = DEFAULT_DUCK_AMOUNT;
  private mono = false;
  private proximity = true;

  constructor(
    readonly ctx: BaseAudioContext,
    opts: SpatialMixerOptions = {},
  ) {
    this.gainCfg = opts.gain ?? DEFAULT_GAIN;
    this.interpDelayMs = opts.interpDelayMs ?? DEFAULT_INTERP_DELAY_MS;
    this.now = opts.now ?? (() => performance.now());
    this.wallNow = opts.wallNow ?? (() => Date.now());
    this.output = ctx.createGain();
    this.output.channelCount = 2;
    this.output.channelCountMode = 'explicit';
    this.output.channelInterpretation = 'speakers';
    setParamNow(this.output.gain, 1, ctx.currentTime);
  }

  // ------------------------------------------------------------- ducking

  /**
   * Abbassa (active=true) o riporta al 100% le voci in arrivo con una rampa morbida:
   * attacco ~50 ms, rilascio ~400 ms. Agisce sul bus d'uscita, dopo volume master e deafen.
   */
  setDucking(active: boolean, amount: number = this.duckAmount): void {
    const a = clamp(amount, 0, 1);
    if (active === this.duckActive && a === this.duckAmount) return;
    const rising = duckGain(active, a) < duckGain(this.duckActive, this.duckAmount);
    this.duckActive = active;
    this.duckAmount = a;
    // Niente cancelScheduledValues: interromperebbe la rampa in corso con un salto (click).
    this.output.gain.setTargetAtTime(duckGain(active, a), this.ctx.currentTime, rising ? DUCK_ATTACK_TAU_S : DUCK_RELEASE_TAU_S);
  }

  isDucking(): boolean {
    return this.duckActive;
  }

  // ------------------------------------------------------------- configuration

  configure(gain: GainConfig, interpDelayMs: number): void {
    this.gainCfg = gain;
    this.interpDelayMs = interpDelayMs;
  }

  getGainConfig(): GainConfig {
    return this.gainCfg;
  }

  setMode(mode: AudioMode): void {
    this.mode = mode;
  }

  getMode(): AudioMode {
    return this.mode;
  }

  /** Audio mono (true) o stereo con direzione 3D (false). Si applica al prossimo update. */
  setMono(mono: boolean): void {
    if (mono === this.mono) return;
    this.mono = mono;
    for (const p of this.peers.values()) p.panner.panningModel = mono ? 'equalpower' : 'HRTF';
  }

  isMono(): boolean {
    return this.mono;
  }

  /** Effetto prossimità: il volume cala con la distanza (true) o resta pieno nel raggio (false). */
  setProximity(on: boolean): void {
    this.proximity = on;
  }

  getProximity(): boolean {
    return this.proximity;
  }

  setMasterVolume(v: number): void {
    this.master = clamp(v, 0, 2);
  }

  setDeafened(d: boolean): void {
    this.deafened = d;
  }

  setPeerVolume(identity: string, v: number): void {
    this.peerVolume.set(identity, clamp(v, 0, 2));
  }

  setPeerMuted(identity: string, muted: boolean): void {
    if (muted) this.peerMuted.add(identity);
    else this.peerMuted.delete(identity);
  }

  /** Radio flag from the `peers` message (the per-frame flag also counts). */
  setPeerRadio(identity: string, radio: boolean): void {
    if (radio) this.radioPeers.add(identity);
    else this.radioPeers.delete(identity);
  }

  // ------------------------------------------------------------- peers

  /**
   * Creates the node chain of a newly subscribed track. Gain starts at 0 (entry ramp, no pop).
   * `key` identifies the track (the same identity can briefly have two tracks during a
   * make-before-break room switch); it defaults to the identity.
   */
  addPeer(identity: string, key: string = identity): AudioNode[] {
    const existing = this.peers.get(key);
    if (existing) return [existing.panner, existing.gain, existing.sink];
    const panner = this.ctx.createPanner();
    panner.panningModel = this.mono ? 'equalpower' : 'HRTF';
    panner.distanceModel = 'linear';
    panner.rolloffFactor = 0;
    panner.refDistance = 1;
    panner.maxDistance = 10_000;
    panner.coneInnerAngle = 360;
    panner.coneOuterAngle = 360;
    panner.coneOuterGain = 1;
    setParamNow(panner.positionX, FRONT.x, this.ctx.currentTime);
    setParamNow(panner.positionY, FRONT.y, this.ctx.currentTime);
    setParamNow(panner.positionZ, FRONT.z, this.ctx.currentTime);
    const gain = this.ctx.createGain();
    setParamNow(gain.gain, 0, this.ctx.currentTime);
    panner.connect(gain);
    gain.connect(this.output);
    // livekit collegherà gain -> sink -> (suo GainNode) -> ctx.destination: solo silenzio.
    const sink = this.ctx.createGain();
    setParamNow(sink.gain, 0, this.ctx.currentTime);
    if (this.monitor) gain.connect(this.monitor.bus);
    const nodes: PeerNodes = {
      key,
      identity,
      panner,
      gain,
      sink,
      addedAt: this.now(),
      audibleSince: null,
      lastGain: 0,
      lastPos: { ...FRONT },
      lastTeleportAt: null,
      state: { identity, distance: null, gain: 0, position: { ...FRONT }, radio: false, policyMuted: false },
    };
    this.peers.set(key, nodes);
    return [panner, gain, sink];
  }

  removePeer(key: string): void {
    const p = this.peers.get(key);
    if (!p) return;
    this.peers.delete(key);
    try {
      p.panner.disconnect();
      p.gain.disconnect();
      p.sink.disconnect();
    } catch {
      /* already disconnected */
    }
  }

  hasPeer(key: string): boolean {
    return this.peers.has(key);
  }

  /** Clears positions (new room / new epoch). Nodes stay. */
  resetSpatial(): void {
    this.buffer.clear();
    this.clock.reset();
  }

  // ------------------------------------------------------------- frames

  pushFrame(frame: Pick<SpatialMessage, 't' | 'self' | 'p'>): void {
    this.clock.observe(frame.t, this.wallNow());
    this.buffer.push(frame);
  }

  /** Listener yaw (degrees, Minecraft) at the last update. */
  getListenerYaw(): number {
    return this.listenerYaw;
  }

  /**
   * One state per identity: the loudest track if an identity has two during a room switch, plus
   * identities present in recent `spatial` frames whose track is not attached yet.
   */
  getPeerStates(): PeerAudioState[] {
    const byId = new Map<string, PeerAudioState>();
    for (const p of this.peers.values()) {
      const prev = byId.get(p.identity);
      if (!prev || p.state.gain > prev.gain) byId.set(p.identity, { ...p.state, position: { ...p.state.position } });
    }
    const renderT = this.lastRenderT;
    if (renderT !== null && this.mode === 'spatial') {
      for (const id of this.buffer.peerIds()) {
        if (byId.has(id)) continue;
        const e = this.evaluate(id, renderT);
        if (!e || e.lastT < renderT - STALE_MS) continue;
        const userVol = this.peerMuted.has(id) ? 0 : (this.peerVolume.get(id) ?? 1);
        byId.set(id, { identity: id, distance: e.distance, gain: e.base * userVol, position: e.pos, radio: e.radio, policyMuted: e.policyMuted });
      }
    }
    return [...byId.values()];
  }

  getPeerState(identity: string): PeerAudioState | null {
    return this.getPeerStates().find((s) => s.identity === identity) ?? null;
  }

  // ------------------------------------------------------------- render

  private evaluate(identity: string, renderT: number) {
    const s = this.buffer.samplePeer(identity, renderT);
    if (!s) return null;
    const off = { x: s.x, y: s.y, z: s.z };
    const distance = length(off);
    const radio = this.radioPeers.has(identity) || (s.flags & SPATIAL_FLAG_RADIO) !== 0;
    const policyMuted = (s.flags & SPATIAL_FLAG_POLICY_MUTED) !== 0;
    let base = proximityGain(distance, this.gainCfg, this.proximity);
    if (radio) base = Math.max(base, RADIO_FLOOR_GAIN);
    if (policyMuted) base = 0;
    return { distance, radio, policyMuted, base, pos: pannerPosition(off, this.listenerYaw), teleportAt: s.teleportAt, lastT: s.lastT };
  }

  /** One render step. Public for tests; normally driven by start(). */
  update(): void {
    const local = this.now();
    const serverNow = this.clock.serverNow(this.wallNow());
    const renderT = serverNow === null ? null : serverNow - this.interpDelayMs;
    this.lastRenderT = renderT;
    const t = this.ctx.currentTime;
    const self = renderT === null ? null : this.buffer.sampleSelf(renderT);
    if (self) this.listenerYaw = self.yaw;
    const masterFactor = this.deafened ? 0 : this.master;

    for (const p of this.peers.values()) {
      let base = 0;
      let pos: Vec3 = FRONT;
      let snap = false;
      let distance: number | null = null;
      let radio = this.radioPeers.has(p.identity);
      let policyMuted = false;

      if (this.mode === 'global') {
        base = 1;
      } else if (renderT !== null) {
        const e = this.evaluate(p.identity, renderT);
        if (e) {
          ({ distance, radio, policyMuted, base, pos } = e);
          if (e.teleportAt !== null && e.teleportAt !== p.lastTeleportAt) {
            snap = true;
            p.lastTeleportAt = e.teleportAt;
          }
          // First known position: jump there (the gain is still 0, nothing audible moves).
          if (p.state.distance === null) snap = true;
        }
      }

      if (base > 0 && p.audibleSince === null) p.audibleSince = local;
      const entry = p.audibleSince === null ? 0 : clamp((local - p.audibleSince) / ENTRY_RAMP_MS, 0, 1);
      const userVol = this.peerMuted.has(p.identity) ? 0 : (this.peerVolume.get(p.identity) ?? 1);
      const g = base * entry * userVol * masterFactor * (this.mono ? MONO_GAIN_COMPENSATION : 1);
      // Mono: il panner riceve sempre "davanti" (stessa voce a sinistra e a destra); la posizione
      // vera resta nello stato per il Radar.
      const panPos = this.mono ? FRONT : pos;

      p.state = { identity: p.identity, distance, gain: base * userVol, position: { ...pos }, radio, policyMuted };

      if (snap) {
        setParamNow(p.panner.positionX, panPos.x, t);
        setParamNow(p.panner.positionY, panPos.y, t);
        setParamNow(p.panner.positionZ, panPos.z, t);
      } else if (
        Math.abs(panPos.x - p.lastPos.x) > EPS ||
        Math.abs(panPos.y - p.lastPos.y) > EPS ||
        Math.abs(panPos.z - p.lastPos.z) > EPS
      ) {
        p.panner.positionX.setTargetAtTime(panPos.x, t, PARAM_TIME_CONSTANT_S);
        p.panner.positionY.setTargetAtTime(panPos.y, t, PARAM_TIME_CONSTANT_S);
        p.panner.positionZ.setTargetAtTime(panPos.z, t, PARAM_TIME_CONSTANT_S);
      }
      p.lastPos = { ...panPos };
      if (Math.abs(g - p.lastGain) > EPS) {
        p.gain.gain.setTargetAtTime(g, t, PARAM_TIME_CONSTANT_S);
        p.lastGain = g;
      }
    }
    if (renderT !== null) this.buffer.prune(renderT);
  }

  start(): void {
    if (this.running) return;
    this.running = true;
    document.addEventListener('visibilitychange', this.onVisibility);
    this.schedule();
  }

  stop(): void {
    this.running = false;
    document.removeEventListener('visibilitychange', this.onVisibility);
    this.cancelScheduled();
  }

  dispose(): void {
    this.stop();
    for (const id of [...this.peers.keys()]) this.removePeer(id);
    try {
      this.output.disconnect();
    } catch {
      /* ignore */
    }
    if (this.monitor) {
      this.monitor.bus.disconnect();
      this.monitor = null;
    }
  }

  // ------------------------------------------------------------- test monitor (§11.2)

  /**
   * Taps every peer gain into a stereo analyser pair (left/right). Used by the E2E hook
   * `window.__voiceTest` to measure tone energy and direction (E2, E3).
   */
  enableMonitor(fftSize = 8192): { left: AnalyserNode; right: AnalyserNode } {
    if (this.monitor) return { left: this.monitor.left, right: this.monitor.right };
    const bus = this.ctx.createGain();
    bus.channelCount = 2;
    bus.channelCountMode = 'explicit';
    bus.channelInterpretation = 'speakers';
    const splitter = this.ctx.createChannelSplitter(2);
    const left = this.ctx.createAnalyser();
    const right = this.ctx.createAnalyser();
    left.fftSize = fftSize;
    right.fftSize = fftSize;
    bus.connect(splitter);
    splitter.connect(left, 0);
    splitter.connect(right, 1);
    this.monitor = { bus, left, right };
    for (const p of this.peers.values()) p.gain.connect(bus);
    return { left, right };
  }

  // ------------------------------------------------------------- loop

  private readonly onVisibility = () => {
    if (!this.running) return;
    this.cancelScheduled();
    this.schedule();
  };

  private schedule(): void {
    if (!this.running) return;
    const hidden = typeof document !== 'undefined' && document.hidden;
    if (!hidden && typeof requestAnimationFrame === 'function') {
      this.rafId = requestAnimationFrame(() => {
        this.rafId = null;
        this.tick();
      });
    } else if (!this.ticker) {
      // Scheda nascosta: ticker in un Web Worker, non rallentato da Chrome (vedi backgroundTicker.ts).
      this.ticker = startBackgroundTicker(HIDDEN_TICK_MS, () => {
        if (this.running) this.update();
      });
    }
  }

  private tick(): void {
    if (!this.running) return;
    try {
      this.update();
    } finally {
      this.schedule();
    }
  }

  private cancelScheduled(): void {
    if (this.rafId !== null) cancelAnimationFrame(this.rafId);
    this.rafId = null;
    this.ticker?.stop();
    this.ticker = null;
  }
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, v));
}

function setParamNow(param: AudioParam, value: number, t: number): void {
  param.cancelScheduledValues(t);
  param.setValueAtTime(value, t);
}
